import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from '../services/api';

interface MigrationFormProps {
  setResponse: (response: any) => void;
}

const schema = yup.object({
  usercpanel: yup.string().required("Le nom d'utilisateur cPanel est requis"),
  passwordcpanel: yup.string().required('Le mot de passe cPanel est requis'),
  urlcpanel: yup.string().url('URL invalide').required("L'URL cPanel est requise"),
  domain_name: yup.string().required('Nom de domaine requis'),
  localdomain: yup.string().url('URL invalide').required('Maquette URL requise'),
}).required();

type FormValues = yup.InferType<typeof schema>;

type LogEntry = {
  id: string;
  timestamp: string;
  job_id: string;
  status: 'info' | 'success' | 'error';
  domain?: string;
  localdomain?: string;
  message: string;
  details?: string; // JSON string
};

type SiteSummary = {
  domain?: string;
  url?: string;
  login_url?: string;
  admin_url?: string;
};

const LOGS_KEY = 'migrationLogs';

// MigrationForm renders the deployment form on the left and logs on the right (scrollable).
const MigrationForm: React.FC<MigrationFormProps> = ({ setResponse }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const pollingRef = React.useRef<number | null>(null);
  const [toolsChecking, setToolsChecking] = useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [siteSummary, setSiteSummary] = useState<SiteSummary | null>(null);
  const logsEndRef = React.useRef<HTMLDivElement | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOGS_KEY);
      if (raw) setLogs(JSON.parse(raw));
    } catch (e) {
      // ignore
    }
  }, []);

  // Fetch recent server logs once on mount so the table shows Flask logs (miseen ligne)
  useEffect(() => {
    let cancelled = false;
    const fetchServerLogs = async () => {
      try {
        const res = await api.get('/api/logs', { params: { limit: 200 } });
        const serverLogs: LogEntry[] = res.data?.logs || [];
        if (!cancelled && serverLogs.length) mergeServerLogs(serverLogs);
      } catch (e) {
        // ignore network/server errors - we don't remove anything
      }
    };
    fetchServerLogs();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(LOGS_KEY, JSON.stringify(logs));
    } catch (e) {
      // ignore
    }
  }, [logs]);

  // Auto-scroll vers le bas quand les logs changent
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const { control, handleSubmit, formState } = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      usercpanel: '',
      passwordcpanel: '',
      urlcpanel: '',
      domain_name: '',
      localdomain: '',
    } as any,
  });

  const { errors } = formState;

  const pushLog = (entry: LogEntry) => setLogs(prev => [entry, ...prev]);

  const mergeServerLogs = (serverLogs: LogEntry[]) => {
    setLogs(prev => {
      const map = new Map<string, LogEntry>();
      // existing logs
      prev.forEach(l => {
        map.set(l.id, l);
      });
      // new logs overwrite
      serverLogs.forEach(l => {
        map.set(l.id, l);
      });

      // produce array sorted by timestamp desc
      const arr = Array.from(map.values()).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      return arr;
    });
  };

  const checkTools = async (jobIdValue?: string, domain?: string, localdomain?: string) => {
    setToolsChecking(true);
    try {
      await api.get('/api/tools', { params: { job_id: jobIdValue, domain, localdomain } });
      if (jobIdValue) {
        const res = await api.get('/api/logs', { params: { job_id: jobIdValue, limit: 200 } });
        const serverLogs: LogEntry[] = res.data?.logs || [];
        mergeServerLogs(serverLogs);
      } else {
        const res = await api.get('/api/logs', { params: { limit: 200 } });
        const serverLogs: LogEntry[] = res.data?.logs || [];
        mergeServerLogs(serverLogs);
      }
    } catch (e) {
      // ignore tool check errors
    } finally {
      setToolsChecking(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = {
        usercpanel: data.usercpanel,
        passwordcpanel: data.passwordcpanel,
        urlcpanel: data.urlcpanel,
        // include legacy/top-level fields because the server reports they are required
        domain_name: data.domain_name,
        localdomain: data.localdomain,
        // keep domains array for backward compatibility
        domains: [{ domain_name: data.domain_name, localdomain: data.localdomain }],
      };
      // Debug: log payload to help diagnose 400 errors from the server
      console.debug('POST /compress payload:', payload);

      const response = await api.post('/compress', payload);
      setResponse({ success: true, data: response.data });
      setMessage({ type: 'success', text: 'Déploiement lancé avec succès.' });
      // Try to extract summary information from server response, fallback to form data
      try {
        const resp = response?.data || {};
        const url = resp.url || resp.site_url || resp.deployed_url || payload.localdomain || payload.domain_name ? (resp.url || resp.site_url || resp.deployed_url || `https://${payload.domain_name}`) : undefined;
        const login = resp.login_url || resp.admin_url || (url ? `${String(url).replace(/\/$/, '')}/wp-login.php` : undefined);
        const admin = resp.admin_url || resp.panel_url || undefined;
        setSiteSummary({ domain: payload.domain_name, url, login_url: login, admin_url: admin });
      } catch (e) {
        // ignore extraction errors
      }
      // If server returns job_id, start polling logs for this job
      const returnedJobId = response?.data?.job_id;
      if (returnedJobId) {
        setJobId(String(returnedJobId));
        // immediately fetch server logs once
        try {
          const res = await api.get('/api/logs', { params: { job_id: returnedJobId, limit: 100 } });
          const serverLogs: LogEntry[] = res.data?.logs || [];
          mergeServerLogs(serverLogs);
        } catch (e) {
          // ignore fetch error for now
        }
        // Fetch server tool availability and log it for this job
        await checkTools(String(returnedJobId), data.domain_name, data.localdomain);
        // also fetch recent global server logs (in case server recorded related entries)
        try {
          const res2 = await api.get('/api/logs', { params: { limit: 200 } });
          const globalLogs: LogEntry[] = res2.data?.logs || [];
          mergeServerLogs(globalLogs);
        } catch (e) {
          // ignore
        }
      } else {
        // fallback local push if no job_id
        pushLog({ id: Date.now().toString(), job_id: '', timestamp: new Date().toISOString(), domain: data.domain_name, localdomain: data.localdomain, status: 'success', message: 'Succès' });
        // also try to fetch global server logs after fallback
        try {
          const res3 = await api.get('/api/logs', { params: { limit: 200 } });
          const globalLogs2: LogEntry[] = res3.data?.logs || [];
          mergeServerLogs(globalLogs2);
        } catch (e) {
          // ignore
        }
      }
    } catch (error: any) {
      // Log full error details to console for debugging (status + response body)
      console.error('POST /compress error', error?.response?.status, error?.response?.data, error);
      const errText = error?.response?.data || 'Une erreur est survenue';
      setResponse({ success: false, error: errText });
      setMessage({ type: 'error', text: String(errText) });
      pushLog({ id: Date.now().toString(), job_id: jobId || '', timestamp: new Date().toISOString(), domain: data.domain_name, localdomain: data.localdomain, status: 'error', message: String(errText) });
    } finally {
      setLoading(false);
    }
  };

  // Polling effect: when jobId is set, poll /api/logs?job_id=... every 5s
  useEffect(() => {
    if (!jobId) return;

    let stopped = false;

    const fetchOnce = async () => {
      try {
        const res = await api.get('/api/logs', { params: { job_id: jobId, limit: 200 } });
        const serverLogs: LogEntry[] = res.data?.logs || [];
        if (!stopped) mergeServerLogs(serverLogs);
      } catch (e) {
        // ignore polling errors
      }
    };

    // start immediate fetch
    fetchOnce();

    pollingRef.current = window.setInterval(fetchOnce, 5000);

    return () => {
      stopped = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobId]);

  const clearLogs = () => {
    setLogs([]);
    localStorage.removeItem(LOGS_KEY);
  };

  const toggleLogDetails = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const renderJsonDetails = (details: string) => {
    try {
      const obj = JSON.parse(details);
      return <pre className="log-details-pre">{JSON.stringify(obj, null, 2)}</pre>;
    } catch (e) {
      return <>{details}</>;
    }
  };

  return (
    <Box ref={containerRef} sx={{ 
      position: 'relative',
      height: '100vh', 
      width: '100vw', 
      m: 0, 
      p: 0, 
      overflow: 'hidden' 
    }}>
      {/* FORM SECTION - LEFT SIDE - FIXED */}
      <Box sx={{ 
        position: 'fixed',
        left: 0,
        top: 0,
        width: '420px',
        height: '100vh',
        overflow: 'auto', 
        p: 2, 
        borderRight: '1px solid rgba(255,255,255,0.1)',
        zIndex: 10,
        backgroundColor: '#0b1220'
      }}>
        <Paper elevation={6} sx={{ p: 3, height: 'fit-content' }}>
          <Stack spacing={2}>
            <Typography variant="h4" component="h1" className="form-title" sx={{ color: '#ffffff' }}>
              DigiiGo
            </Typography>

            {message && (
              <Alert severity={message.type} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}

            {/* Site summary shown after a successful deploy */}
            {siteSummary && (
              <Box sx={{ mt: 1, bgcolor: 'rgba(31, 138, 118, 0.08)', p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>Récapitulatif du site déployé</Typography>
                <Stack spacing={1.5}>
                  {siteSummary.domain && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Typography sx={{ fontWeight: 500, minWidth: 100 }}>Domain:</Typography>
                      <Typography>{siteSummary.domain}</Typography>
                    </Box>
                  )}
                  {siteSummary.url && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 500, minWidth: 100 }}>URL:</Typography>
                      <Button size="small" variant="text" href={siteSummary.url} target="_blank" rel="noreferrer">
                        {siteSummary.url}
                      </Button>
                    </Box>
                  )}
                  {siteSummary.login_url && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography sx={{ fontWeight: 500, minWidth: 100 }}>Connexion:</Typography>
                      <Button size="small" variant="contained" color="primary" href={siteSummary.login_url} target="_blank" rel="noreferrer">
                        Se connecter
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                <Controller
                  name="usercpanel"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="cPanel User" required fullWidth error={!!errors.usercpanel} helperText={errors.usercpanel?.message as any} />
                  )}
                />

                <Controller
                  name="passwordcpanel"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="cPanel Password" type="password" required fullWidth error={!!errors.passwordcpanel} helperText={errors.passwordcpanel?.message as any} />
                  )}
                />

                <Controller
                  name="urlcpanel"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="cPanel URL" required fullWidth error={!!errors.urlcpanel} helperText={errors.urlcpanel?.message as any} />
                  )}
                />

                <Typography variant="h6">Domain</Typography>

                <Controller
                  name="domain_name"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Domain Name" required fullWidth error={!!errors.domain_name} helperText={errors.domain_name?.message as any} />
                  )}
                />

                <Controller
                  name="localdomain"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} label="Maquette URL" required fullWidth error={!!errors.localdomain} helperText={errors.localdomain?.message as any} />
                  )}
                />

                <Button type="submit" variant="contained" color="primary" disabled={loading} fullWidth size="large" startIcon={loading ? <CircularProgress size={18} /> : null}>
                  {loading ? 'Déploiement...' : 'Déployer'}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Paper>
      </Box>

      {/* LOGS SECTION - RIGHT SIDE */}
      <Box sx={{ 
        position: 'absolute',
        left: '420px',
        top: 0,
        right: 0,
        height: '100vh',
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        m: 0,
        p: 0
      }}>
        <Paper elevation={6} className="logs-shell" sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', m: 0, borderRadius: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" className="logs-header" sx={{ p: 2, flexShrink: 0 }}>
            <Box>
              <Typography variant="h6" className="logs-title">Journal de mise en ligne</Typography>
              <Typography variant="body2" className="logs-subtitle">Suivi temps reel des etapes, erreurs et outils disponibles.</Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" onClick={() => checkTools(jobId || undefined)} disabled={toolsChecking}>
                {toolsChecking ? 'Verification...' : 'Verifier outils'}
              </Button>
              <Button size="small" onClick={clearLogs} color="inherit">Effacer</Button>
            </Stack>
          </Stack>

          <Box className="logs-scroller" sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
            {logs.length === 0 ? (
              <Box className="log-empty">
                <Typography variant="body1">Aucun log pour l'instant</Typography>
                <Typography variant="body2">Lance une mise en ligne pour voir les etapes ici.</Typography>
              </Box>
            ) : (
              <Box className="log-console">
                {logs.map((l) => {
                  const timeStr = new Date(l.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                  const hasDetails = l.details && l.details !== '{}' && l.details !== 'null';
                  const isExpanded = expandedLogs.has(l.id);
                  
                  return (
                    <Box key={l.id} className={`log-line log-${l.status}`}>
                      <Box className="log-line-main">
                        <span className="log-timestamp">[{timeStr}]</span>
                        <span className={`log-badge log-badge-${l.status}`}>{l.status.toUpperCase()}</span>
                        <span className="log-text">{l.message}</span>
                        {hasDetails && (
                          <button 
                            className="log-expand-btn" 
                            onClick={() => toggleLogDetails(l.id)}
                            title={isExpanded ? "Masquer détails" : "Voir détails"}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                      </Box>
                      
                      {isExpanded && hasDetails && l.details && (
                        <Box className="log-line-details">
                          {renderJsonDetails(l.details)}
                        </Box>
                      )}
                      
                      {(l.job_id || l.domain || l.localdomain) && (
                        <Box className="log-line-meta">
                          {l.job_id && <span>job:{l.job_id.substring(0, 8)}</span>}
                          {l.domain && <span>domain:{l.domain}</span>}
                          {l.localdomain && <span>local:{l.localdomain}</span>}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <div ref={logsEndRef} />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MigrationForm;